import { Link } from "@tanstack/react-router";
import {
  MapPin,
  Phone,
  Mail,
  Building2,
  CalendarHeart,
  UtensilsCrossed,
  Package,
  Info,
  HeartHandshake,
  HandHeart,
  CalendarDays,
  MessageCircle,
  Instagram,
  Facebook,
  Linkedin,
} from "lucide-react";
import { useI18n } from "@project/lib/use-i18n";
import logo from "@project/assets/jivandhara-logo.png";

const SOCIAL_LINKS = [
  {
    name: "Instagram",
    href: "https://www.instagram.com/jivandharavruddhashram?stkn=MW5nbzJ4bmpjdnlxcg==",
    icon: Instagram,
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/share/1FdLwVyDMn/?mibextid=wwXIfr",
    icon: Facebook,
  },
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/in/jivandhara-vruddhashram-004a62101?utm_source=share_via&utm_content=profile&utm_medium=member_ios",
    icon: Linkedin,
  },
];

const FOOTER_QUICK_LINKS = [
  {
    to: "/about" as const,
    labelKey: "footer.aboutUs" as const,
    icon: Info,
  },
  {
    to: "/resident-care" as const,
    labelKey: "footer.residentCare" as const,
    icon: HeartHandshake,
  },
  {
    to: "/donate" as const,
    labelKey: "footer.donateLink" as const,
    icon: HandHeart,
  },
  {
    to: "/events" as const,
    labelKey: "footer.events" as const,
    icon: CalendarDays,
  },
  {
    to: "/contact" as const,
    labelKey: "footer.contact" as const,
    icon: MessageCircle,
  },
];

const FOOTER_SERVICES = [
  {
    to: "/gaushala" as const,
    labelKey: "footer.gaushala" as const,
    icon: Building2,
  },
  {
    to: "/bhojanshala" as const,
    labelKey: "footer.bhojanTithi" as const,
    icon: CalendarHeart,
  },
  {
    to: "/tiffin-seva" as const,
    labelKey: "footer.tiffinSeva" as const,
    icon: UtensilsCrossed,
  },
  {
    to: "/gruh-udhyog" as const,
    labelKey: "footer.gruhUdhyog" as const,
    icon: Package,
  },
];

export function SiteFooter() {
  const { t } = useI18n();
  return (
    <footer className="mt-20 bg-primary text-primary-foreground">
      <div className="mx-auto grid max-w-site gap-8 px-4 py-12 md:grid-cols-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <div
                className="bg-gold/25 pointer-events-none absolute inset-0 rounded-full blur-lg"
                aria-hidden
              />
              <div className="ring-gold/60 relative grid h-14 w-14 place-items-center overflow-hidden rounded-full bg-background shadow-lg ring-2">
                <img
                  src={logo}
                  alt="Jivandhara Old Age Home logo"
                  className="h-12 w-12 object-contain"
                />
              </div>
            </div>
            <div>
              <div className="font-serif text-xl font-bold">
                {t("common.siteName")}
              </div>
              <div className="text-gold/80 text-[10px] uppercase tracking-widest">
                {t("common.careCompassionCommunity")}
              </div>
            </div>
          </div>
          <p className="text-primary-foreground/70 mt-3 text-sm">
            {t("common.tagline")}
          </p>
          <div className="mt-4 flex items-center gap-2.5">
            {SOCIAL_LINKS.map(social => (
              <a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.name}
                title={social.name}
                className="hover:border-gold/60 flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition-all duration-200 hover:scale-105 hover:bg-gold hover:text-white"
              >
                <social.icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
        <div>
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gold">
            {t("footer.quickLinks")}
          </h4>
          <ul className="text-primary-foreground/80 space-y-2 text-sm">
            {FOOTER_QUICK_LINKS.map(link => (
              <li key={link.labelKey}>
                <Link
                  to={link.to}
                  className="flex items-center gap-2 transition hover:text-gold"
                >
                  <link.icon className="h-4 w-4 shrink-0 text-gold" />
                  <span>{t(link.labelKey)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gold">
            {t("footer.services")}
          </h4>
          <ul className="text-primary-foreground/80 space-y-2 text-sm">
            {FOOTER_SERVICES.map(service => (
              <li key={service.labelKey}>
                <Link
                  to={service.to}
                  className="flex items-center gap-2 transition hover:text-gold"
                >
                  <service.icon className="h-4 w-4 shrink-0 text-gold" />
                  <span>{t(service.labelKey)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gold">
            {t("footer.reachUs")}
          </h4>
          <ul className="space-y-2.5 text-sm text-primary-foreground/80">
            <li className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <span>{t("footer.address")}</span>
            </li>
            <li className="flex items-start gap-2">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <div className="flex flex-col space-y-1">
                {t("footer.phone")
                  .split(",")
                  .map(p => p.trim())
                  .filter(Boolean)
                  .map(phone => (
                    <a
                      key={phone}
                      href={`tel:${phone.replace(/\s+/g, "")}`}
                      className="transition hover:text-gold"
                    >
                      {phone}
                    </a>
                  ))}
              </div>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <a
                href={`mailto:${t("footer.email")}`}
                className="transition hover:text-gold"
              >
                {t("footer.email")}
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-primary-foreground/10 text-primary-foreground/60 border-t py-4 text-center text-xs">
        © {new Date().getFullYear()} {t("common.siteName")}.{" "}
        {t("footer.rights")}
      </div>
    </footer>
  );
}
